import { zeroize } from '@/crypto/zeroize'

/**
 * VaultUnlockMaterial — LOCKED 中も保持する unlock 用の暗号文 + KDF パラメータ。
 *
 * `KEY_HIERARCHY.md §4.5` と `SESSION_LIFECYCLE.md §4.1` に準拠。
 * AES-256-GCM 暗号文 (`protectedSymmetricKey`) と Argon2id 用 salt + パラメータを
 * 保持し、ロック解除時に MP からの再 KDF + AES-GCM unwrap で SK を復元する。
 *
 * これらは **暗号文 or non-secret な公開素材** なので LOCKED 中もメモリに残す
 * ことが許容される (サーバ DB と等価レベルの秘匿度)。
 */
export interface VaultUnlockMaterial {
  /** Argon2id 用 salt (hex) — prelogin / login レスポンス由来 */
  readonly kdfSalt: string

  /** Argon2id パラメータ */
  readonly kdfParams: {
    readonly memoryCost: number
    readonly timeCost: number
    readonly parallelism: number
  }

  /** Sealed SK (base64) */
  readonly protectedSymmetricKey: string

  /** AES-GCM IV (base64, 12 byte) */
  readonly protectedSymmetricKeyIv: string

  /** SRP に使った email (再入力させず再利用) */
  readonly email: string
}

/**
 * UNAUTH → UNLOCKED 遷移用 snapshot。
 *
 * `loginFlow.ts` の `setSession` から渡される。`accessToken` と SK に加えて、
 * LOCKED 時にも保持すべき `material` を併せて受け取る (T-C3 で追加)。
 */
export interface UnlockedSnapshot {
  readonly accessToken: string
  readonly symmetricKey: Uint8Array
  readonly material: VaultUnlockMaterial
}

/**
 * セッション状態。3 状態を排他的に表現する tagged union。
 *
 *   - `unauth`:   未ログイン。すべてのフィールドなし
 *   - `locked`:   ログイン済だがロック中。SK だけが zeroize 済 (持たない)
 *   - `unlocked`: ログイン済 + 解錠中。SK 含む完全な動作セッション
 *
 * 詳細は `SESSION_LIFECYCLE.md §2`。
 */
export type SessionState =
  | { readonly kind: 'unauth' }
  | {
      readonly kind: 'locked'
      readonly accessToken: string
      readonly material: VaultUnlockMaterial
    }
  | {
      readonly kind: 'unlocked'
      readonly accessToken: string
      readonly symmetricKey: Uint8Array
      readonly material: VaultUnlockMaterial
    }

export type SessionListener = () => void

const UNAUTH: SessionState = { kind: 'unauth' }

let current: SessionState = UNAUTH
const listeners = new Set<SessionListener>()

const notify = (): void => {
  // 同期通知。listener 内の throw が他 listener を阻害しないよう逐次保護。
  for (const listener of [...listeners]) {
    try {
      listener()
    } catch {
      // listener 例外は本ストアの責務外。握りつぶす。
    }
  }
}

export const sessionStore = {
  /**
   * UNAUTH → UNLOCKED 遷移。既存 SK は zeroize し、新 SK + material を保持。
   * LOCKED / UNLOCKED 状態から呼ばれた場合は再ログイン扱いとして同じ遷移を行う
   * (旧 SK を zeroize、新 snapshot で置換、listener 通知)。
   */
  set(next: UnlockedSnapshot): void {
    if (current.kind === 'unlocked') {
      zeroize(current.symmetricKey)
    }
    current = {
      kind: 'unlocked',
      accessToken: next.accessToken,
      symmetricKey: next.symmetricKey,
      material: next.material,
    }
    notify()
  },

  /**
   * UNAUTH → LOCKED 復元 (B5、F5 / タブ再読込後のセッション復元)。
   *
   * `POST /auth/refresh` (HttpOnly cookie) で得た accessToken と、サーバが返した
   * 封印済 `material` から LOCKED 状態を作る。SK は持たない — ユーザーは LockPage で
   * マスターパスワードを入力して `unlock()` する。UNAUTH 以外では no-op
   * (既にセッションがある場合は上書きしない)。
   */
  restoreLocked(accessToken: string, material: VaultUnlockMaterial): void {
    if (current.kind !== 'unauth') return
    current = { kind: 'locked', accessToken, material }
    notify()
  },

  /**
   * UNLOCKED → LOCKED 遷移。SK を zeroize、accessToken + material は保持。
   *
   * UNLOCKED 以外 (UNAUTH / LOCKED) で呼ばれた場合は **no-op** (通知もしない)。
   * `material` の object identity は維持する (新規 alloc しない) — GC 負荷と
   * DevTools 参照追跡を最小化 (`SESSION_LIFECYCLE.md §5.3`)。
   */
  lock(): void {
    if (current.kind !== 'unlocked') return
    zeroize(current.symmetricKey)
    current = {
      kind: 'locked',
      accessToken: current.accessToken,
      material: current.material,
    }
    notify()
  },

  /**
   * LOCKED → UNLOCKED 遷移。MP 再入力 + Argon2id + AES-GCM unwrap で得た SK を保持。
   *
   * UNLOCKED から呼ばれた場合は、既存 SK を zeroize して新 SK で上書きする
   * (再 unlock — 通常起きないが整合性のため)。
   * UNAUTH から呼ばれたら throw — 呼出側のロジック誤りを早期検出。
   */
  unlock(symmetricKey: Uint8Array): void {
    if (current.kind === 'unauth') {
      throw new Error('cannot unlock: session is unauth')
    }
    if (current.kind === 'unlocked') {
      zeroize(current.symmetricKey)
    }
    current = {
      kind: 'unlocked',
      accessToken: current.accessToken,
      symmetricKey,
      material: current.material,
    }
    notify()
  },

  /**
   * Access token のみ差し替える (refresh 用)。
   *
   * UNLOCKED / LOCKED でのみ可。SK / material は触らない。
   * 認証状態は変わらないので listener は通知しない (現行と同じ — `SESSION_LIFECYCLE.md §4.2`)。
   */
  updateAccessToken(accessToken: string): void {
    if (current.kind === 'unauth') {
      throw new Error('cannot update access token: no active session')
    }
    if (current.kind === 'locked') {
      current = {
        kind: 'locked',
        accessToken,
        material: current.material,
      }
    } else {
      current = {
        kind: 'unlocked',
        accessToken,
        symmetricKey: current.symmetricKey,
        material: current.material,
      }
    }
  },

  /**
   * \* → UNAUTH 遷移。SK を zeroize、material も含めて全クリア。
   *
   * 既に UNAUTH なら no-op (通知もしない、現行 `clear()` と同じ semantics)。
   */
  clear(): void {
    if (current.kind === 'unauth') return
    if (current.kind === 'unlocked') {
      zeroize(current.symmetricKey)
    }
    current = UNAUTH
    notify()
  },

  /**
   * 現状を返す。常に valid な `SessionState` を返す (null は返さない)。
   * UNAUTH 時は singleton object `{ kind: 'unauth' }` を返すため、object identity
   * 比較 (`getState() === prev`) で「変化なし」判定が可能。
   */
  getState(): SessionState {
    return current
  },

  /**
   * LOCKED / UNLOCKED 時の material を返す。UNAUTH 時は null。
   * LockPage の unlock 経路で MP + material を組み合わせて SK を復元するときに使う。
   */
  getMaterial(): VaultUnlockMaterial | null {
    return current.kind === 'unauth' ? null : current.material
  },

  /**
   * **UNLOCKED のみ true**。RouteGuard が「保護ルートに入れるか」の判定に使う。
   * LOCKED 中は false を返すため、idle 満了でロックされた瞬間に保護ルートから
   * 蹴り出される動線が成立する (`SESSION_LIFECYCLE.md §4.3`)。
   */
  isAuthenticated(): boolean {
    return current.kind === 'unlocked'
  },

  /** LOCKED のみ true。RouteGuard が「lock route に向けるか」の判定に使う。 */
  isLocked(): boolean {
    return current.kind === 'locked'
  },

  /**
   * 全状態遷移 (set / lock / unlock / clear) を購読する。返される関数で解除。
   * `updateAccessToken` は通知しない (認証状態が変わらないため)。
   *
   * L-15 教訓 + T-C1 round 1 で確立したセマンティクスを維持:
   *   - listener throw 握りつぶし (他 listener を阻害しない)
   *   - Set semantics で重複登録は 1 回のみ実行
   *   - 戻り値関数で解除可能
   */
  subscribe(listener: SessionListener): () => void {
    listeners.add(listener)

    return () => {
      listeners.delete(listener)
    }
  },
}
